import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { Text } from "@/components/ui/baseUi/text"
import { useBoxProgram } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { useBoxSlotDuplicateSession } from "@/modules/boxSlotSession/hooks/useBoxSlotSessionDuplicate"
import { useBoxSlotScheduledSessionsInRange } from "@/modules/boxSlotSession/hooks/useBoxSlotSessionScheduled"
import { useBoxSessionWeekCopy } from "@/modules/boxSlotSession/store/useBoxSessionWeekCopyStore"
import { useCurrentBoxId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import dayjs from "dayjs"
import { useImperativeHandle, useState } from "react"

export type BoxProgrammingWeekCopyModalRef = {
  onPasteWeekClick: () => Promise<void>
}

export type BoxProgrammingWeekCopyModalProps = {
  boxProgramId: string
  ref: React.Ref<BoxProgrammingWeekCopyModalRef>
}

export function BoxProgrammingWeekCopyModal({ boxProgramId, ref }: BoxProgrammingWeekCopyModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const boxId = useCurrentBoxId()
  const { refreshCalendarEvents, selectedDate, getCalendarRange } = useCalendar()
  const { startDate, endDate } = getCalendarRange()
  const { data: boxProgrammation } = useBoxProgram(boxProgramId)
  const { data: scheduledSessions } = useBoxSlotScheduledSessionsInRange(
    boxProgrammation.id,
    dayjs(startDate).toDate(),
    dayjs(endDate).toDate(),
  )
  const weekCopied = useBoxSessionWeekCopy()
  const { onPasteBoxProgrammationSessionWeek } = useBoxSlotDuplicateSession()

  const currentWeekHasSessions = scheduledSessions.length > 0
  const copiedSessionsAreFromDifferentProgram = weekCopied?.boxProgramId !== boxProgrammation.id
  const copiedSessionsAreFromDifferentBoxProgram = boxId !== weekCopied?.boxProgramId

  const title = currentWeekHasSessions
    ? word("box.activity.weekCopyPaste.paste.modale.sessionsAlreadyExist.title")
    : copiedSessionsAreFromDifferentBoxProgram
      ? word("box.activity.weekCopyPaste.paste.modale.pasteFromAnotherBoxProgram.message", {
          originProgramName: weekCopied?.boxProgramName,
          originBoxName: weekCopied?.boxName,
        })
      : copiedSessionsAreFromDifferentProgram
        ? word("box.activity.weekCopyPaste.paste.modale.pasteFromAnotherProgram.title", {
            originProgramName: weekCopied.boxProgramName,
          })
        : ""
  const message =
    copiedSessionsAreFromDifferentProgram && currentWeekHasSessions
      ? copiedSessionsAreFromDifferentBoxProgram
        ? word("box.activity.weekCopyPaste.paste.modale.pasteFromAnotherBoxProgramAndSessionsAlreadyExist.message", {
            originProgramName: weekCopied?.boxProgramName,
            originBoxName: weekCopied?.boxName,
          })
        : word("box.activity.weekCopyPaste.paste.modale.pasteFromAnotherProgramAndSessionsAlreadyExist.message", {
            originProgramName: weekCopied.boxProgramName,
          })
      : currentWeekHasSessions
        ? word("box.activity.weekCopyPaste.paste.modale.sessionsAlreadyExist.message")
        : ""

  const onHide = () => {
    setShowModal(false)
  }

  const onPasteWeek = async (overwrite?: boolean) => {
    setIsSubmitting(true)
    setShowModal(false)

    if (selectedDate) {
      await onPasteBoxProgrammationSessionWeek(boxProgrammation.id, dayjs(selectedDate).weekday(0).toDate(), overwrite)
    }

    setIsSubmitting(false)
    refreshCalendarEvents()
  }

  useImperativeHandle(ref, () => ({
    onPasteWeekClick: async () => {
      if (currentWeekHasSessions || copiedSessionsAreFromDifferentProgram) {
        setShowModal(true)
      } else {
        await onPasteWeek()
      }
    },
  }))

  const onLeftButtonClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (currentWeekHasSessions) {
      await onPasteWeek(true)
    }
    setShowModal(false)
  }

  const onRightButtonClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    await onPasteWeek(false)
  }

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className="mt-3 flex w-[418px] flex-col rounded-4xl">
        {/* HEADER */}
        <DialogHeader>
          <DialogTitle>
            <Text fontStyle="TL" className="text-center">
              {title}
            </Text>
          </DialogTitle>
        </DialogHeader>

        {/* CONTENT */}
        <Text fontStyle="BM" className="text-center wrap-break-word whitespace-break-spaces text-textDefault-caption">
          {message}
        </Text>

        {/* BUTTON */}
        <div className="mb-3 flex flex-row items-center justify-center gap-4">
          <Button
            size="lg"
            variant={currentWeekHasSessions ? "secondary" : "outline"}
            onClick={(event) => void onLeftButtonClick(event)}
            className="flex-1"
          >
            {currentWeekHasSessions
              ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.replace")
              : word("global.cancel")}
          </Button>
          <Button
            size="lg"
            variant={currentWeekHasSessions ? "secondary" : "primary"}
            onClick={(event) => void onRightButtonClick(event)}
            disabled={isSubmitting}
            className="flex-1"
          >
            {currentWeekHasSessions
              ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.keep")
              : word("global.paste")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
